import { storeConfig } from '@/shared/storeConfig';
import { useAuthStore } from '@/stores/auth';

export const useSmarticoShop = defineStore(
  'smarticoShop',
  () => {
    const { debugLog } = useSmarticoConfigs();
    const authStore = useAuthStore();

    // ITENS DA LOJA
    const shopItems = ref<TStoreItem[]>([]);
    const isLoading = ref(false);

    async function loadshopItems(opts?: { force?: boolean }): Promise<void> {
      isLoading.value = true;
      try {
        const { isAuthenticated } = authStore;
        const api = isAuthenticated
          ? window._smartico?.api
          : window._smartico?.vapi('EN');

        // quando force=true, evita caches/localStorage
        if (opts?.force) {
          try {
            await api?.clearCaches?.();
          } catch {}
        }

        // busca itens diretamente da API
        const items = await api?.getStoreItems?.();

        shopItems.value = Array.isArray(items) ? items : [];
      } catch (err: any) {
        debugLog(`⭕️ ~ getStoreItems ~ ${err?.toString?.() || err}`);
      } finally {
        isLoading.value = false;
      }
    }

    // HISTÓRICO DE COMPRAS
    type THistoryItem = any;

    const shopHistory = ref<THistoryItem[]>([]);
    const historyState = ref({
      limit: 100,
      offset: 0,
      hasMore: true,
      isLoading: false,
      pageCap: 0 as number,
    });

    const API_TIMEOUT_MS = 8000;

    async function withTimeout<T>(
      promise: Promise<T>,
      ms = API_TIMEOUT_MS,
    ): Promise<T> {
      return await Promise.race([
        promise,
        new Promise<T>((_resolve, reject) =>
          setTimeout(
            () => reject(new Error('Timeout ao carregar histórico da loja')),
            ms,
          ),
        ),
      ]);
    }

    async function loadShopHistory(opts?: { limit?: number; reset?: boolean }) {
      const { isAuthenticated } = authStore;

      if (!isAuthenticated) {
        debugLog(
          '⭕️ ~ loadShopHistory ~ Usuário não autenticado, pulando carregamento do histórico',
        );

        return;
      }

      const limit = opts?.limit ?? historyState.value.limit;
      const reset = !!opts?.reset;

      if (historyState.value.isLoading) return;
      historyState.value.isLoading = true;

      try {
        const offset = reset ? 0 : historyState.value.offset;

        const wsapi = (window as any)?._smartico?.api;
        const sapi = wsapi?.api;
        const uid = (window as any)?._smartico_user_id;

        let res: any;

        if (sapi && typeof sapi.storeGetPurchasedItems === 'function') {
          res = await withTimeout(
            sapi.storeGetPurchasedItems(uid ?? null, limit, offset),
          );
        } else if (sapi && typeof sapi.storeGetPurchasedItemsT === 'function') {
          const itemsT = await withTimeout(
            sapi.storeGetPurchasedItemsT(uid ?? null, limit, offset),
          );

          const itemsTArray = Array.isArray(itemsT) ? itemsT : [];

          res = { items: itemsTArray, hasMore: itemsTArray.length === limit };
        } else if (typeof wsapi?.getStorePurchasedItems === 'function') {
          await wsapi?.clearCaches?.();
          res = await withTimeout(wsapi.getStorePurchasedItems(limit, offset));
        } else if (typeof wsapi?.getStorePurchasedItemsT === 'function') {
          await wsapi?.clearCaches?.();

          const itemsT = await withTimeout(
            wsapi.getStorePurchasedItemsT(limit, offset),
          );

          const itemsTArray = Array.isArray(itemsT) ? itemsT : [];

          res = { items: itemsTArray, hasMore: itemsTArray.length === limit };
        } else {
          throw new TypeError(
            'Nenhuma função de histórico disponível na API Smartico',
          );
        }

        const items: THistoryItem[] = Array.isArray(res)
          ? (res as any)
          : (res?.items ?? []);

        // 1) Atualiza cap real observado (tipicamente 20 na sua label)
        const observedCap = items.length || historyState.value.pageCap || 20;

        historyState.value.pageCap = historyState.value.pageCap
          ? Math.max(historyState.value.pageCap, observedCap)
          : observedCap;

        const hasMore =
          typeof res?.hasMore === 'boolean'
            ? !!res.hasMore
            : items.length === historyState.value.pageCap;

        if (reset) shopHistory.value = items;
        else shopHistory.value = shopHistory.value.concat(items);

        historyState.value.offset = offset + items.length;
        historyState.value.limit = limit;
        historyState.value.hasMore = hasMore;
      } catch (err: any) {
        debugLog(`⭕️ ~ getStorePurchasedItems ~ ${err?.toString?.() || err}`);
      } finally {
        historyState.value.isLoading = false;
      }
    }

    async function loadShopHistoryAll(opts?: {
      limit?: number;
      reset?: boolean;
    }) {
      const limit = opts?.limit ?? historyState.value.limit;
      const reset = opts?.reset ?? true;

      await loadShopHistory({ limit, reset });

      let guard = 0;

      while (historyState.value.hasMore && guard < 200) {
        const effectiveLimit = historyState.value.pageCap || limit;
        const before = shopHistory.value.length;

        await loadShopHistory({ limit: effectiveLimit });

        if (shopHistory.value.length === before) break;
        guard++;
      }
    }

    async function loadShopHistorySimple(opts?: {
      limit?: number;
      reset?: boolean;
    }) {
      const limit = opts?.limit ?? 30;
      const reset = opts?.reset ?? true;

      await loadShopHistory({ limit, reset });
    }

    function resetShopHistory() {
      shopHistory.value = [];
      historyState.value = {
        limit: 30,
        offset: 0,
        hasMore: true,
        isLoading: false,
        pageCap: 0 as number,
      };
    }

    async function buyShopItem(storeItemId: number) {
      const { isAuthenticated } = authStore;

      if (!isAuthenticated) {
        throw new Error('É necessário estar logado para comprar itens da loja');
      }

      try {
        return await window._smartico.api.buyStoreItem(storeItemId);
      } catch (err: any) {
        debugLog(`⭕️ ~ buyStoreItem ~ ${err?.toString?.() || err}`);
        throw err;
      }
    }

    return {
      // loja
      shopItems,
      isLoading,
      loadshopItems,
      buyShopItem,

      // histórico
      shopHistory,
      historyState,
      loadShopHistory,
      loadShopHistoryAll,
      loadShopHistorySimple,
      resetShopHistory,
    };
  },
  storeConfig,
);
